import { useState, useRef, useCallback, useEffect } from 'react';
import RecordRTC from 'recordrtc';

interface UseAudioRecorderProps {
    onRecordingComplete?: (blob: Blob) => void;
    maxDuration?: number; // in seconds
    deviceId?: string; // optional mic device to record from; omit for default device
}

interface AudioRecorderReturn {
    isRecording: boolean;
    duration: number;
    audioLevel: number;
    waveformBars: number[];
    startRecording: () => Promise<void>;
    stopRecording: () => void;
    cancelRecording: () => void;
    pauseRecording: () => void;
    resumeRecording: () => void;
}

export const useAudioRecorder = ({
    onRecordingComplete,
    maxDuration = 30,
    deviceId
}: UseAudioRecorderProps = {}): AudioRecorderReturn => {
    const [isRecording, setIsRecording] = useState(false);
    const [duration, setDuration] = useState(0);
    const [audioLevel, setAudioLevel] = useState(0);
    const [waveformBars, setWaveformBars] = useState<number[]>(Array(50).fill(0));

    const recorderRef = useRef<RecordRTC | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    const analyserRef = useRef<AnalyserNode | null>(null);
    const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const animationFrameRef = useRef<number | null>(null);
    const visualizationCleanupRef = useRef<(() => void) | null>(null);
    const recordingSessionRef = useRef<number>(0); // Track recording session
    const isMountedRef = useRef<boolean>(true); // Track whether the hook is still mounted

    // Cleanup function
    const cleanup = useCallback(() => {
        // Invalidate current session
        recordingSessionRef.current += 1;

        // Stop and destroy recorder first
        if (recorderRef.current) {
            try {
                if (recorderRef.current.getState() === 'recording') {
                    recorderRef.current.stopRecording();
                }
                recorderRef.current.destroy();
            } catch (e) {
                console.log('Recorder cleanup error:', e);
            }
            recorderRef.current = null;
        }

        // Stop visualization animation
        if (visualizationCleanupRef.current) {
            visualizationCleanupRef.current();
            visualizationCleanupRef.current = null;
        }

        // Stop animation frame
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }

        // Clear timer
        if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
        }

        // Close audio context
        if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
            audioContextRef.current.close().catch(err => console.log('Context close error:', err));
            audioContextRef.current = null;
        }

        // Stop media stream
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }

        // Reset analyser
        analyserRef.current = null;

        // Reset state
        setAudioLevel(0);
        setWaveformBars(Array(50).fill(0));
        setIsRecording(false);
        setDuration(0);
    }, []);

    // Audio visualization
    const startVisualization = useCallback(() => {
        if (!analyserRef.current) return;

        const analyser = analyserRef.current;
        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        const barCount = 50;
        let isActive = true;

        const updateAudioLevel = () => {
            if (!analyserRef.current || !isActive) return;

            analyser.getByteFrequencyData(dataArray);

            // Calculate RMS for overall level
            let sum = 0;
            for (let i = 0; i < bufferLength; i++) {
                const normalized = dataArray[i] / 255;
                sum += normalized * normalized;
            }
            const rms = Math.sqrt(sum / bufferLength);
            const level = Math.min(rms * 4, 1);
            setAudioLevel(level);

            // Create individual bar heights from frequency data
            const bars: number[] = [];
            const samplesPerBar = Math.floor(bufferLength / barCount);

            for (let i = 0; i < barCount; i++) {
                const start = i * samplesPerBar;
                const end = start + samplesPerBar;
                let barSum = 0;

                for (let j = start; j < end; j++) {
                    barSum += dataArray[j];
                }

                const barAvg = barSum / samplesPerBar;
                const normalized = barAvg / 255;
                const positionBoost = 1 + (1 - i / barCount) * 0.5;
                const scaled = Math.pow(normalized, 0.6) * 2.5 * positionBoost;
                bars.push(Math.min(scaled, 1));
            }

            setWaveformBars(bars);
            animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
        };

        updateAudioLevel();

        // Return cleanup function
        return () => {
            isActive = false;
        };
    }, []);

    // Start recording
    const startRecording = useCallback(async () => {
        try {
            // Clean up any existing recording first
            cleanup();

            // Increment session ID to invalidate any old timers
            recordingSessionRef.current += 1;
            const currentSession = recordingSessionRef.current;

            // Get media stream — fall back to the default device if the requested one disappeared.
            let stream: MediaStream;
            try {
                stream = await navigator.mediaDevices.getUserMedia({
                    audio: deviceId
                        ? {
                            deviceId: { exact: deviceId },
                            echoCancellation: true,
                            noiseSuppression: true,
                            autoGainControl: true
                        }
                        : {
                            echoCancellation: true,
                            noiseSuppression: true,
                            autoGainControl: true
                        }
                });
            } catch (err) {
                if (deviceId && err instanceof Error && err.name === 'OverconstrainedError') {
                    stream = await navigator.mediaDevices.getUserMedia({
                        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
                    });
                } else {
                    throw err;
                }
            }

            // The await above can resolve AFTER the component has unmounted or
            // after another start/stop invalidated this session. If so, the
            // stream we just acquired would be orphaned — held by a dead
            // instance with no code path left to stop it, leaving the browser's
            // mic indicator stuck on. Detect that and release it immediately.
            if (!isMountedRef.current || currentSession !== recordingSessionRef.current) {
                stream.getTracks().forEach(track => track.stop());
                return;
            }

            streamRef.current = stream;

            // Setup audio visualization
            const audioContext = new AudioContext();
            const source = audioContext.createMediaStreamSource(stream);
            const analyser = audioContext.createAnalyser();
            analyser.fftSize = 2048;
            analyser.smoothingTimeConstant = 0.75;
            source.connect(analyser);

            audioContextRef.current = audioContext;
            analyserRef.current = analyser;

            // Setup RecordRTC for WAV recording
            const recorder = new RecordRTC(stream, {
                type: 'audio',
                mimeType: 'audio/wav',
                recorderType: RecordRTC.StereoAudioRecorder,
                numberOfAudioChannels: 1,
                desiredSampRate: 16000,
                timeSlice: 1000,
                ondataavailable: (blob: Blob) => {
                    // Optional: handle data chunks if needed
                }
            });

            recorder.startRecording();
            recorderRef.current = recorder;

            // Start timer - reset to 0
            setDuration(0);
            setIsRecording(true);

            let currentDuration = 0;
            timerIntervalRef.current = setInterval(() => {
                // Check if this is still the current session
                if (currentSession !== recordingSessionRef.current) {
                    return; // Stale timer, ignore
                }
                
                currentDuration += 1;
                setDuration(currentDuration);
                
                // Auto-stop at max duration
                if (currentDuration >= maxDuration) {
                    stopRecording();
                }
            }, 1000);

            // Start visualization
            const cleanupFn = startVisualization();
            visualizationCleanupRef.current = cleanupFn || null;

        } catch (error) {
            console.error('Error starting recording:', error);
            alert('Failed to start recording. Please check microphone permissions.');
            cleanup();
        }
    }, [maxDuration, deviceId, startVisualization, cleanup]);

    // Releases the mic/timers/visualization synchronously, shared by both stopRecording
    // (finalize + report the blob) and cancelRecording (discard, never report a blob).
    // The browser keeps the "mic active" indicator lit as long as ANY holder of the input
    // stream is alive: the media tracks themselves OR an AudioContext with a live
    // MediaStreamSource node on the mic. We have two such contexts (our visualization
    // context + RecordRTC's internal one). Tear them all down right now instead of waiting
    // for RecordRTC's async merge callback, which may be delayed or (under a strict CSP
    // that blocks blob: workers) never fire.
    const releaseRecordingResources = useCallback(() => {
        // 1. Invalidate any pending timer session and clear the timer.
        recordingSessionRef.current += 1;
        if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
        }

        // 2. Stop the visualization animation loop.
        if (visualizationCleanupRef.current) {
            visualizationCleanupRef.current();
            visualizationCleanupRef.current = null;
        }
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }

        // 3. Close our visualization AudioContext (holds a mic source node).
        if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
            audioContextRef.current.close().catch(err => console.log('Context close error:', err));
            audioContextRef.current = null;
        }
        analyserRef.current = null;

        // 4. Stop the actual microphone tracks.
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }

        // 5. Close RecordRTC's shared/internal AudioContext synchronously.
        // RecordRTC only closes this inside the async destroy() callback below,
        // but until it's closed the browser keeps a mic source node alive on
        // the device — which, for Bluetooth headsets, keeps the headset (HFP)
        // profile and the "mic active" indicator on even after the track ends.
        // The blob is rebuilt from buffered PCM, not this context, so closing
        // it now is safe.
        try {
            const storage = (RecordRTC as any).Storage;
            const sharedContext = storage?.AudioContextConstructor;
            if (sharedContext && sharedContext.state !== 'closed') {
                sharedContext.close().catch((err: unknown) => console.log('Shared context close error:', err));
            }
            // Null RecordRTC's reference so its own destroy() (in the async
            // callback below) doesn't try to close this context a second time —
            // closing an already-closed AudioContext throws. RecordRTC lazily
            // recreates it on the next recording.
            if (storage) {
                storage.AudioContextConstructor = null;
            }
        } catch (e) {
            console.log('Shared context close exception:', e);
        }

        setAudioLevel(0);
        setWaveformBars(Array(50).fill(0));
    }, []);

    
    const stopRecording = useCallback(() => {
        if (!recorderRef.current) return;

        setIsRecording(false);
        const recorder = recorderRef.current;
        releaseRecordingResources();

        // --- Finalize the recording asynchronously ---
        // Produce the blob, then destroy the recorder (which closes
        // RecordRTC's internal AudioContext). The mic is already released
        // above, so the indicator turns off even if this never resolves.
        recorder.stopRecording(() => {
            const blob = recorder.getBlob();

            if (blob && onRecordingComplete) {
                onRecordingComplete(blob);
            }

            try {
                recorder.destroy();
            } catch (e) {
                console.log('Recorder destroy error:', e);
            }
            if (recorderRef.current === recorder) {
                recorderRef.current = null;
            }
            setDuration(0);
        });
    }, [onRecordingComplete, releaseRecordingResources]);

    
    const cancelRecording = useCallback(() => {
        if (!recorderRef.current) return;

        setIsRecording(false);
        const recorder = recorderRef.current;
        releaseRecordingResources();

        recorder.stopRecording(() => {
            try {
                recorder.destroy();
            } catch (e) {
                console.log('Recorder destroy error:', e);
            }
            if (recorderRef.current === recorder) {
                recorderRef.current = null;
            }
            setDuration(0);
        });
    }, [releaseRecordingResources]);

    // Pause recording
    const pauseRecording = useCallback(() => {
        if (recorderRef.current) {
            recorderRef.current.pauseRecording();
            
            if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current);
                timerIntervalRef.current = null;
            }
        }
    }, []);

    // Resume recording
    const resumeRecording = useCallback(() => {
        if (recorderRef.current) {
            recorderRef.current.resumeRecording();
            
            timerIntervalRef.current = setInterval(() => {
                setDuration((prev) => {
                    const newDuration = prev + 1;
                    if (newDuration >= maxDuration) {
                        stopRecording();
                    }
                    return newDuration;
                });
            }, 1000);
        }
    }, [maxDuration, stopRecording]);

    // Cleanup on unmount
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            // Mark unmounted and invalidate any in-flight getUserMedia so a
            // stream that resolves after this point gets stopped, not orphaned.
            isMountedRef.current = false;
            recordingSessionRef.current += 1;

            // Cleanup all resources when component unmounts
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
            if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current);
            }
            if (recorderRef.current) {
                try {
                    recorderRef.current.stopRecording();
                } catch (e) {
                    // Ignore errors on unmount
                }
            }
            if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
                audioContextRef.current.close().catch(() => {});
            }
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
            }
        };
    }, []); // Empty dependency array - only run on mount/unmount

    return {
        isRecording,
        duration,
        audioLevel,
        waveformBars,
        startRecording,
        stopRecording,
        cancelRecording,
        pauseRecording,
        resumeRecording
    };
};

